import axios, { AxiosInstance } from 'axios';
import { getStoredToken, clearAuthData } from '@/utils/auth';
import { initiateOAuthLogin } from '@/utils/oauth';
import { categorizeError } from './errors';
import { resolveTenantFromUrl } from '@low6-pickem/frontend-shared';

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:3001';

// Tenant slug resolved from URL path or environment variable
const TENANT_SLUG = resolveTenantFromUrl();

// Track if OAuth redirect is in progress to prevent multiple simultaneous redirects
let isOAuthRedirectPending = false;

/**
 * Create an Axios instance for API calls
 */
export const apiClient: AxiosInstance = axios.create({
  baseURL: API_URL,
  headers: {
    'Content-Type': 'application/json',
    'X-Tenant-Slug': TENANT_SLUG,
  },
  timeout: 30000,
});

/**
 * Request interceptor (for adding auth tokens, X-Tenant-Slug header)
 */
apiClient.interceptors.request.use(
  config => {
    // Explicitly ensure X-Tenant-Slug header is set
    if (!config.headers['X-Tenant-Slug'] && !config.headers['x-tenant-slug']) {
      config.headers['X-Tenant-Slug'] = TENANT_SLUG;
    }

    // Add auth token if available
    const token = getStoredToken();

    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  error => {
    return Promise.reject(error);
  }
);

/**
 * Response interceptor (for error handling and OAuth redirect on 401)
 */
apiClient.interceptors.response.use(
  response => {
    // Return data directly (unwrap response.data)
    return response.data;
  },
  error => {
    const status = error.response?.status;

    // Categorize error for better handling
    const apiError = categorizeError(error);

    // Handle 401 Unauthorized - trigger OAuth login
    if (status === 401) {
      // Check if OAuth redirect is already pending
      if (isOAuthRedirectPending) {
        return Promise.reject(apiError);
      }

      // Check if we're already in the OAuth callback flow to prevent infinite loop
      const isOAuthCallback =
        window.location.pathname.includes('/auth/callback') ||
        window.location.hash.includes('/auth/callback');

      if (isOAuthCallback) {
        return Promise.reject(apiError);
      }

      // Check if we have a stored token - if we do and it's being rejected,
      // there might be a backend issue, so don't immediately retry OAuth
      const storedToken = getStoredToken();
      if (storedToken) {
        clearAuthData();
        return Promise.reject(apiError);
      }

      clearAuthData();

      // Mark OAuth redirect as pending
      isOAuthRedirectPending = true;

      // Trigger OAuth login flow
      initiateOAuthLogin().catch(() => {
        // Reset flag on error so user can try again
        isOAuthRedirectPending = false;
      });
    }

    // Reject with categorized error for better error handling downstream
    return Promise.reject(apiError);
  }
);

export default apiClient;
